import { Action, Reducer } from 'redux';
import { AppAction } from '../actions/App';
import * as ActionTypes from '../actions/ActionTypes';
import { AppUser } from '../../interfaces/AppUser';
import { Organization } from '../../interfaces/Organization';
import { CurrencyRate } from '../../interfaces/Vehicle';
import { NotificationDetails, NotificationTranslation, UserNotification } from '../../interfaces/UserNotificationsUpdate';

export interface AppState {
  user: any;
  appUser: AppUser | null;
  showMessage: boolean;
  language: string;
  dateFormat: string;
  longDateFormat: string;
  userNotifications: UserNotification[];
  currencyRates: CurrencyRate[];
  token: string | null;
  refreshToken: string | null;
  userOrganizations: Organization[];
  isGeneralLoading: boolean;
}

const initialState: AppState = {
  user: null,
  appUser: null,
  showMessage: false,
  language: '',
  dateFormat: '',
  longDateFormat: '',
  userNotifications: [],
  currencyRates: [],
  token: null,
  refreshToken: null,
  userOrganizations: [],
  isGeneralLoading: false,
};

const getTranslationsForNotification = (translations: NotificationTranslation[], fieldName: string, language: string): string | null => {
  try {
      const indexLang = translations.findIndex((trans: any) => trans.fieldName === fieldName && trans.language === language);
      if (indexLang !== -1) return translations[indexLang].name;
      return null;
  }
  catch (e) { 
      return null;
  }
};

export const AppReducer: Reducer<AppState | undefined> = (
  state: AppState | undefined,
  incomingAction: Action
): AppState | undefined => {
  if (state === undefined) {
    return initialState;
  }

  const action = incomingAction as AppAction;
  const language = state?.language;
              
  switch (action.type) {
    case ActionTypes.APP_SET_USER: {
      return {
        ...state,
        user: action.user,
        appUser: action.appUser
      };
    }

    case ActionTypes.APP_SET_USER_ORGANIZATIONS:
      return {
        ...state,
        userOrganizations: action.userOrganizations
      };
      
    case ActionTypes.APP_DISPLAY_MESSAGE:
      return {
        ...state,
        showMessage: action.display
      };
    case ActionTypes.APP_REMOVE_USER:
      return {
        ...state,
        user: null,
        appUser: null
      };

    case ActionTypes.APP_SET_LANGUAGE:
      return {
        ...state,
        language: action.language,
        dateFormat: action.dateFormat,
        longDateFormat: action.longDateFormat
      };
      
    case ActionTypes.APP_LOAD_NOTIFICATION:
    {
      const extendedUserNotifications: UserNotification[] = action.userNotifications.map((un: UserNotification) => {
          try {
              if (un.detailsJson === null || un.detailsJson === undefined) {
                  return un;
              }

              const details: NotificationDetails = JSON.parse(un.detailsJson); 
              
              if (details.translations === null || details.translations === undefined) {
                  return { ...un, details };
              }

              details.statusDisplayName = 
                getTranslationsForNotification(details.translations, 'StatusDisplayName', language) || details.statusDisplayName;
              details.categoryDisplayName = 
                getTranslationsForNotification(details.translations, 'CategoryDisplayName', language) || details.statusDisplayName;
              return { ...un, details };
          } 
          catch (e) {
              console.error('Error parsing detailsJson for notification', un, e);
              // Provide a default NotificationDetails object to satisfy the type
              const defaultDetails: NotificationDetails = {
                // Fill with appropriate default values for NotificationDetails
                // Example:
                id: 0,
                statusDisplayName: 'No json available',
                statusId: 0,
                statusDate: un.dateCreation,
                categoryDisplayName: '',
                vehiclePlateNumber: '',
                organizationOwnerName: '',
                caseNumber: '',
                userName: '',
                userHoName: '',
                text: '',
                caseId: 0,
                translations: []
              };
              return { ...un, details: defaultDetails };
          }
      });

      return {
        ...state,
        userNotifications: extendedUserNotifications        
      };
    }

    case ActionTypes.APP_REMOVE_NOTIFICATION:
      const notificationList = [...state.userNotifications];
      const index = notificationList.findIndex((item) => item.id === action.id);
      notificationList.splice(index, 1);

      return {
        ...state,
        userNotifications: notificationList
      };
    case ActionTypes.APP_MARK_AS_VIEWED_NOTIFICATION:
        const notificationListForMark = [...state.userNotifications];
        const indexForMark = notificationListForMark.findIndex((item) => item.id === action.id);
        notificationListForMark[indexForMark].read = true;
  
        return {
          ...state,
          userNotifications: notificationListForMark
    };

    case ActionTypes.APP_SET_CURRENCY_RATES:
      return {
        ...state,
        currencyRates: action.currencyRates
      };

    case ActionTypes.APP_SET_TOKEN:
      return {
        ...state,
        token: (action as any).token
      };

    case ActionTypes.APP_SET_REFRESH_TOKEN:
      return {
        ...state,
        refreshToken: (action as any).refreshToken
      };

    case ActionTypes.APP_SET_GENERAL_LOADING:
      return {
        ...state,
        isGeneralLoading: (action as any).isGeneralLoading
      };
  }

  return state;
};
